import {
    SendResetPasswordResponse,
    User,
    UserDeleteResponse,
    UserListResponse,
    UserPagination,
} from "../../modules/admin/users/users.type";
import {Injectable} from "@angular/core";
import {BehaviorSubject, filter, map, Observable, of, switchMap, take, tap, throwError} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {environment} from "../../../environments/environment";


@Injectable({providedIn: 'root'})
export class UserService {
    private path = 'users';
    private _user: BehaviorSubject<User | null> = new BehaviorSubject(null);
    private _users: BehaviorSubject<User[] | null> = new BehaviorSubject(null);
    private _pagination: BehaviorSubject<any> = new BehaviorSubject(null);

    constructor(private _httpClient: HttpClient) {
    }

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: User) {
        // Store the value
        this._user.next(value);
    }

    get users$(): Observable<User[]> {
        return this._users.asObservable();
    }

    get user$(): Observable<User> {
        return this._user.asObservable();
    }

    get currentUser(): User | null {
        return this._user.getValue();
    }

    get pagination$(): Observable<any> {
        return this._pagination.asObservable();
    }

    getUsers(page: number = 1, size: number = 10, sort: string = 'name', order: 'asc' | 'desc' | '' = 'asc', search: string = ''): Observable<User[]> {
        return this._httpClient.get<{ pagination: UserPagination; users: User[] }>(environment.api + this.path, {
            params: {
                page: '' + page,
                limit: size,
                sort,
                order,
                search,
            }
        }).pipe(
            tap((response: any) => {
                this._pagination.next(response.pagination);
                this._users.next(response.users);
            }),
        );
    }

    getUsersByCompanyId(companyId: string): Observable<User[]> {
        return this._httpClient.get<{users: User[] }>(environment.api + this.path + '/company/' + companyId).pipe(
            tap((response: any) => {
                //this._pagination.next(response.pagination);
                this._users.next(response.users);
            }),
        );
    }

    /**
     * Fetch a paginated page of users without touching the legacy BehaviorSubject state.
     * Prefer this over `getUsers()` in new components.
     */
    listUsers(page: number = 1, limit: number = 10, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc', search: string = ''): Observable<UserListResponse> {
        return this._httpClient.get<UserListResponse>(environment.api + this.path, {
            params: {
                page: '' + page,
                limit: '' + limit,
                sort,
                order,
                search,
            },
        });
    }

    /**
     * Fetch a single user without mutating the shared `_user` subject
     * (that subject holds the *logged in* admin).
     */
    fetchUser(id: string): Observable<User> {
        return this._httpClient.get<User>(environment.api + this.path + '/' + id);
    }

    /**
     * Permanently delete a user. The API cascades to Stripe customers and the Front App contact.
     */
    deleteUser(id: string): Observable<UserDeleteResponse> {
        return this._httpClient.delete<UserDeleteResponse>(environment.api + this.path + '/' + id);
    }

    /**
     * Send a password reset link to the user's email address.
     */
    sendResetPassword(id: string): Observable<SendResetPasswordResponse> {
        return this._httpClient.post<SendResetPasswordResponse>(
            environment.api + this.path + '/' + id + '/send-reset-password',
            {},
        );
    }

    getUserById(id: string): Observable<User> {
        return this._httpClient.get<User>(environment.api + this.path + '/' + id).pipe(
            map((user) => {
                // Update the course
                this._user.next(user);

                // Return the course
                return user;
            }),
            switchMap((user) => {
                if (!user) {
                    return throwError(() => new Error('Could not find user with id of ' + id + '!'));
                }

                return of(user);
            }),
        );
    }

    delete(id: string): Observable<boolean> {
        return this.users$.pipe(
            take(1),
            switchMap(users => this.deleteUser(id).pipe(
                map((response: UserDeleteResponse) => {
                    // Keep the cached list in sync
                    const index = (users || []).findIndex(item => item._id === id);

                    if (index > -1) {
                        users.splice(index, 1);
                        this._users.next(users);
                    }

                    return response?.success !== false;
                }),
            )),
        );
    }

    uploadAvatar(id: string, profile_image: File): Observable<User> {
        return this.users$.pipe(
            take(1),
            switchMap(users => this._httpClient.post<User>(environment.api + this.path + '/profile_image', {
                id,
                profile_image,
            }, {
                headers: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'Content-Type': profile_image.type,
                },
            }).pipe(
                map((updatedUser) => {
                    // Find the index of the updated contact
                    const index = users.findIndex(item => item._id === id);

                    // Update the contact
                    users[index] = updatedUser;

                    // Update the contacts
                    this._users.next(users);

                    // Return the updated contact
                    return updatedUser;
                }),
                switchMap(updatedUser => this.user$.pipe(
                    take(1),
                    filter(item => item && item._id === id),
                    tap(() => {
                        // Update the contact if it's selected
                        this._user.next(updatedUser);

                        // Return the updated contact
                        return updatedUser;
                    }),
                )),
            )),
        );
    }

    update(id: string, user: User): Observable<User> {
        return this.users$.pipe(
            take(1),
            switchMap(users => this._httpClient.patch<User>(environment.api + this.path + '/' + id, {
                user,
            }).pipe(
                map((updatedUser) => {
                    // Find the index of the updated contact
                    const index = users.findIndex(item => item._id === id);

                    // Update the contact
                    users[index] = updatedUser;

                    // Update the contacts
                    this._users.next(users);

                    // Return the updated contact
                    return updatedUser;
                }),
                switchMap(updatedUser => this.user$.pipe(
                    take(1),
                    filter(item => item && item._id === id),
                    tap(() => {
                        // Update the contact if it's selected
                        this._user.next(updatedUser);

                        // Return the updated contact
                        return updatedUser;
                    }),
                )),
            )),
        );
    }
}
